import { DetectionZone, Device, Facility, FacilityCurrentOccupancy, FacilityOccupancy, ScreenLine } from "@app/shared";
import { useMemo } from "react";
import { useLocalStorage } from "react-use";
import { useFacilityCoordsHelper } from "../../../../helpers";
import { useFacilityCenter } from "../../../../helpers/mapHelpers";
import { CircleMapShape, MapShape, OmniGoogleMap } from "../../../shared/maps";
import { FacilityCameraFallback } from "./FacilityCameraFallback";
import { FacilityOccupancyOverlay } from "./FacilityOccupancyOverlay";

type ParkingViewMapPanelProps = {
    facility: Facility;
    currentOccupancy: FacilityOccupancy | undefined;
    occupancy: FacilityCurrentOccupancy | undefined;
    timeZone: string;
    onEstablishBaseline: () => void;
    /** Superadmin URL override — render the no-coordinates fallback even when coords exist. */
    forceNoCoords?: boolean;
    /**
     * Whether to draw the occupancy readout over the lot. Ground truth and the count it feeds are
     * superadmin-only while the feature settles, and the orchestrator owns that decision — this
     * panel just honours it. See FacilityParkingView.
     */
    showOccupancy?: boolean;
};

function confidenceToOpacity(occupationConfidence: number): number | undefined {
    if (occupationConfidence > .85) { return 1; }
    if (occupationConfidence < .3) { return 0; }
    return occupationConfidence;
}

/**
 * The top half of the Parking View: the lot, with the occupancy overlay on it. Falls back to a
 * camera snapshot when the facility has no coordinates to center a map on.
 */
export const ParkingViewMapPanel: React.FC<ParkingViewMapPanelProps> = ({
    facility, currentOccupancy, occupancy, timeZone, onEstablishBaseline, forceNoCoords, showOccupancy = true,
}) => {
    const [centerCoords] = useFacilityCenter(facility);
    const [hideDevices] = useLocalStorage(`facility_parkingview_hidedevices_${facility.id}`, false);
    const [hideOutlines] = useLocalStorage(`facility_parkingview_hideoutlines_${facility.id}`, false);
    const fCoords = useFacilityCoordsHelper(facility);

    const facilityShapes = useMemo(
        () => [
            ...(hideDevices ? [] : facility.devices ?? []),
            ...(hideOutlines ? [] : facility.detectionZones ?? []),
        ] as Array<Device | ScreenLine | DetectionZone>,
        [facility, hideDevices, hideOutlines]);

    const zoneCenters = useMemo(() => {
        const centers = {} as { [idx: string]: google.maps.LatLng };
        facility.detectionZones?.forEach(z => {
            if (z.id && z.points?.length) {
                const bounds = new google.maps.LatLngBounds();
                z.points?.forEach(pt => bounds.extend(fCoords?.toLatLng(pt)!));
                centers[z.id] = bounds.getCenter();
            }
        });
        return centers;
    }, [facility.detectionZones, fCoords]);

    const overlay = showOccupancy
        ? <FacilityOccupancyOverlay occupancy={occupancy} timeZone={timeZone} onEstablishBaseline={onEstablishBaseline} />
        : null;

    if (forceNoCoords || !centerCoords) {
        return <FacilityCameraFallback devices={facility.devices ?? []}>{overlay}</FacilityCameraFallback>;
    }

    return (
        <div className="relative h-full w-full">
            <OmniGoogleMap center={centerCoords} fitToPolygons={true} noWrap={true}>
                {facilityShapes.map(mp => (<MapShape facility={facility} key={mp.id} shape={mp} />))}
                {zoneCenters && currentOccupancy?.zoneOccupancy?.map(s => s.isOccupied &&
                    <CircleMapShape
                        key={s.id}
                        position={zoneCenters[s.detectionZoneId!]}
                        color={facility.devices?.find(d => d.id === s.deviceId)?.color}
                        opacity={confidenceToOpacity(s.isOccupiedConfidence)}
                        radius={5}
                    />)}
            </OmniGoogleMap>
            {overlay}
        </div>
    );
};
